import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, FileText, Building, Loader2, AlertCircle } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setData,
  setUserInitiatedNav,
} from "../../../store/slices/oldConventionalSlice";
import { useLocation, useNavigate } from "react-router-dom";
import OldConventionalDetails from "../ViewForms/OldConventionalDetails";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import declarationDocument from "../../../assets/documents/declaration.pdf";

const particularsSchema = z.object({
  terms_accepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
  applicant_signature: z
    .any()
    .refine(
      (file) => file instanceof File || typeof file === "string",
      "Signature is required"
    ),
  declaration_document: z
    .any()
    .refine(
      (file) => file instanceof File || typeof file === "string",
      "Declaration Document is required"
    ),
});

const PurchaseDeclarationDetails = ({ data, districts, communities }) => {
  const [filePreviews, setFilePreviews] = useState({
    applicant_signature: null,
  });
  const location = useLocation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const [showConfirmation, setShowConfirmation] = useState(false); // Modal state
  const [thankyouModal, setThankyouModal] = useState(false);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema),
    defaultValues: {
      terms_accepted: data?.purchase_declaration?.terms_accepted ?? false,
      applicant_signature: data?.purchase_declaration?.applicant_signature ?? undefined,
      declaration_document:
        data?.purchase_declaration?.declaration_document ?? undefined,
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data?.purchase_declaration?.applicant_signature) {
      setFilePreviews((prev) => ({
        ...prev,
        applicant_signature: data?.purchase_declaration?.applicant_signature,
        declaration_document: data?.purchase_declaration?.declaration_document,
      }));
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      setValue(name, file, { shouldValidate: true });
      await trigger(name);
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setButtonType("final_submit");

    // Validate form first
    const isValid = await trigger();

    if (!isValid) {
      toast({
        title: "Validation Error",
        description: "Please fix all errors before submitting",
        variant: "destructive",
      });
      return;
    }

    // Show confirmation modal
    setShowConfirmation(true);
  };

  const confirmSubmission = async () => {
    setShowConfirmation(false);
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      submitData.append("applicant_signature", formData.applicant_signature);
      submitData.append("declaration_document", formData.declaration_document);
      submitData.append("id", data?.id || "");
      submitData.append(
        "declaration_accepted",
        formData?.terms_accepted == true ? 1 : 0
      );

      const response = await api.post(
        "/new_shuttleless/purchase_declaration",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      await dispatch(setData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application submitted successfully",
        variant: "success",
      });

      searchParams.delete("tab"); 
      dispatch(setUserInitiatedNav(true));
      nav("/")
    } catch (error) {
      console.error("Submission error:", error);
      if (error.response?.data?.status === "validation_error") {
        const validationErrors = error.response.data.errors;
        Object.entries(validationErrors).forEach(([field, messages]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            toast({
              title: `Validation Error - ${field}`,
              description: messages[0],
              variant: "destructive",
            });
          }
        });

        const firstErrorField = Object.keys(validationErrors)[0];
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.focus();
          }
        }
      } else if (error.response?.data?.message) {
        toast({
          title: "Submission Failed",
          description: error.response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Submission Failed",
          description: "Failed to submit application. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* <OldConventionalDetails application={data} view_page={false} /> */}
        <Card className="mt-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Terms and Conditions:
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 mt-5">
            <div className="prose prose-sm max-w-none p-4 bg-muted rounded-md overflow-y-auto">
              <ul className="list-disc pl-4 space-y-1 text-sm">
                <li>
                  The applicant should be a genuine powerloom owner/operator
                  engaged in textile production.
                </li>
                <li>
                  The subsidy will be provided as per the approved value in the
                  guidelines of the scheme.
                </li>
                <li>
                  All supporting documents must be genuine and verifiable.
                </li>
                <li>
                  The modernized looms should be operational for a minimum
                  period as specified in the scheme guidelines.
                </li>
                <li>
                  The applicant should not have been blacklisted by any
                  Government agency.
                </li>
                <li>
                  Submission of false information may lead to cancellation of
                  application and legal action.
                </li>
                <li>
                  The decision of the sanctioning authority will be final and
                  binding.
                </li>
                <li>
                  The scheme is subject to availability of budget allocation.
                </li>
                <li>
                  All statutory clearances and permissions are the
                  responsibility of the applicant.
                </li>
                <li>
                  It has been insisted that the applicant should not have avail
                  / had availed the subsidy/schemes/benefits <br /> for the
                  components under this scheme from any other central or state
                  Government.
                </li>
              </ul>
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="terms_accepted"
                  checked={formData.terms_accepted || false}
                  onCheckedChange={(checked) =>
                    handleInputChange("terms_accepted", Boolean(checked))
                  }
                />
                <Label
                  htmlFor="terms_accepted"
                  className="text-sm cursor-pointer"
                >
                  I have read and understood the above terms and conditions.{" "}
                  <span className="text-red-500">*</span>
                </Label>
              </div>

              {errors.terms_accepted && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {errors.terms_accepted.message}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="declaration_document">
                Declaration Document <span className="text-red-500"> *</span>
                <Button
                  type="button"
                  variant="link"
                  onClick={() =>
                    handleDownload(declarationDocument, `declaration.pdf`)
                  }
                >
                  (Download Declaration document)
                </Button>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="declaration_document"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) =>
                    handleFileUpload("declaration_document", e.target.files[0])
                  }
                  className={
                    errors.declaration_document ? "border-red-500" : ""
                  }
                />

                {formData.declaration_document instanceof File && (
                  <p className="text-sm text-green-600">
                    {formData.declaration_document.name}
                  </p>
                )}

                {typeof filePreviews.declaration_document === "string" && (
                  <a
                    href={filePreviews.declaration_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    View uploaded document
                  </a>
                )}

                {errors.declaration_document && (
                  <p className="text-sm text-red-500">
                    {errors.declaration_document.message}
                  </p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground">
                  <b>
                    Download the declaration document, take a printout, and sign
                    it with the date and place and Upload.
                  </b>
                  <br />
                  Supported only PDF, JPG, JPEG, PNG Files max size 2MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="applicant_signature">
                Signature of the Applicant{" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="applicant_signature"
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileUpload("applicant_signature", e.target.files[0])
                  }
                  className={errors.applicant_signature ? "border-red-500" : ""}
                />

                {formData.applicant_signature instanceof File && (
                  <p className="text-sm text-green-600">
                    {formData.applicant_signature.name}
                  </p>
                )}

                {typeof filePreviews.applicant_signature === "string" && (
                  <a
                    href={filePreviews.applicant_signature}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    View uploaded document
                  </a>
                )}

                {errors.applicant_signature && (
                  <p className="text-sm text-red-500">
                    {errors.applicant_signature.message}
                  </p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only JPG, JPEG, PNG Files max size 2MB
                </p>
              </div>
            </div>

            <div className="flex justify-end mt-6 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
                disabled={isSubmitting}
                className="min-w-[150px]"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="min-w-[150px] bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  "Final Submit"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent
          className="sm:max-w-md"
          disableCloseButton
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Confirm Final Submission
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p className="font-medium text-foreground">
                  Are you sure you want to submit your application?
                </p>
                <ul className="list-disc pl-4 space-y-1 text-sm text-muted-foreground">
                  <li>This action cannot be undone</li>
                  <li>
                    You will not be able to edit your application after
                    submission
                  </li>
                  <li>Your application will be sent for review and approval</li>
                  <li>
                    Please ensure all information is correct before proceeding
                  </li>
                  <li>
                    If Application is found incorrect my application will be
                    rejected at any point of time
                  </li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmation(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmSubmission}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                "Yes, Submit Application"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={thankyouModal} onOpenChange={setThankyouModal}>
        <DialogContent
          disableCloseButton
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Your Application Submitted Successfully
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p className="font-medium text-foreground">
                  Your Application Number is {data?.application_no}
                </p>
                <p className="text-foreground">
                  Your application will be sent to your concern district
                  officer. You receive a provisional sanction order once your
                  application is verified and approved by the district office.
                  You will also get a SMS and Email update for further status of
                  the application.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              type="button"
              onClick={() =>
                handleDownload(
                  data?.application_url,
                  `${data?.application_no}.pdf`
                )
              }
            >
              Download Application
            </Button>
            <Button
              type="button"
              onClick={() => {
                setThankyouModal(false);
                nav("/");
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Go To DashBoard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PurchaseDeclarationDetails;
